import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Percent, Plus, Search, Pen, Trash2, X as IconX, SplitSquareHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  gstRatesOf, addGst, updateGst, deleteGst, splitGst,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { CustomSelect } from '../../components/CustomSelect';
import { IconAction, RowActions } from '../../components/RowActions';

const blank = (orgId) => ({ id: null, orgId, name: '', rate: '' });

const Modal = ({ open, draft, setDraft, onClose, onSubmit, mode, accent, showNotification, rates }) => {
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) setErrors({});
  }, [open]);

  if (!open) return null;
  const split = splitGst(Number(draft.rate) || 0);

  const handleSubmit = () => {
    const newErrors = {};
    if (!draft.name || !draft.name.trim()) newErrors.name = 'Please fill the Label.';
    if (draft.rate === '' || draft.rate === null || isNaN(draft.rate) || Number(draft.rate) < 0 || Number(draft.rate) > 100) newErrors.rate = 'Please provide a valid Rate between 0 and 100.';

    // One slab, one row.
    //
    // The label is free text and the rate is the slab, so nothing stopped 5%
    // being entered twice under two names. Both then appeared in every product
    // form as separate choices that tax identically, and a report grouped by
    // rate split one slab's takings over two lines. Caught here as well as at
    // the server because the row is added to the list before the request is
    // sent - a server refusal alone would leave the duplicate on screen.
    if (!newErrors.rate) {
      const clash = (rates || []).find((g) => g.id !== draft.id && Number(g.rate) === Number(draft.rate));
      if (clash) newErrors.rate = `${clash.name} is already ${Number(clash.rate)}%. Edit that rate instead of adding a second one.`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    onSubmit(draft);
  };
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-[var(--theme-tertiary)] overflow-hidden flex flex-col"
        >
          <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }}><Percent size={18} /></div>
              <div>
                <h3 className="text-lg font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit GST rate' : 'New GST rate'}</h3>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Used on products & services</p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
          </header>

          <div className="px-6 py-5 flex flex-col gap-4">
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Label <span className="text-rose-500 ml-1">*</span>
              </span>
              <input
                type="text"
                value={draft.name}
                onChange={(e) => {
                  setDraft({ ...draft, name: e.target.value });
                  if (errors.name) setErrors({ ...errors, name: undefined });
                }}
                placeholder="e.g. GST 18%"
                className={`w-full rounded-lg bg-gray-50 border ${errors.name ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`}
              />
              {errors.name && <p className="text-[11px] font-medium text-rose-500">{errors.name}</p>}
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Rate (%) <span className="text-rose-500 ml-1">*</span>
              </span>
              <input
                type="number"
                min="0"
                step="0.5"
                value={draft.rate}
                placeholder="e.g. 18"
                onChange={(e) => {
                  setDraft({ ...draft, rate: e.target.value });
                  if (errors.rate) setErrors({ ...errors, rate: undefined });
                }}
                className={`w-full rounded-lg bg-gray-50 border ${errors.rate ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`}
              />
              {errors.rate && <p className="text-[11px] font-medium text-rose-500">{errors.rate}</p>}
            </label>
            <div className="rounded-lg border border-[var(--theme-tertiary)] p-3" style={{ background: 'var(--theme-secondary)' }}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2 inline-flex items-center gap-1.5">
                <SplitSquareHorizontal size={11} aria-hidden="true" /> Local-sale split
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">CGST</p>
                  <p className="text-sm font-extrabold text-[#0d0d0d]">{split.cgst}%</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">SGST</p>
                  <p className="text-sm font-extrabold text-[#0d0d0d]">{split.sgst}%</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">IGST*</p>
                  <p className="text-sm font-extrabold text-slate-300">{Number(draft.rate) || 0}%</p>
                </div>
              </div>
              <p className="text-[11px] font-medium text-slate-400 mt-2">*IGST applies to future inter-state sales.</p>
            </div>
          </div>

          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]"
              style={{ background: accent }}
            >
              {mode === 'edit' ? 'Save' : 'Create GST rate'}
            </button>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  rates: PropTypes.array,
};



export const GstTab = ({ org, showNotification }) => {
  const { version: dataVersion } = useSharedStore();
  const [version, setVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [draft, setDraft] = useState(blank(org.id));
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Every rate the shop has, before the search box narrows it - the check for
  // a rate already in use has to see the ones a search is hiding.
  const all = useMemo(
    () => gstRatesOf(org.id),
    [org.id, version, dataVersion],
  );

  const list = useMemo(() => {
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((g) => (g.name || '').toLowerCase().includes(q) || String(g.rate).includes(q));
  }, [all, search]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(list.length / perPage));
  const start = (page - 1) * perPage;
  const pageList = list.slice(start, start + perPage);

  const accent = org.theme.primary;

  const openCreate = () => { setDraft(blank(org.id)); setMode('create'); setModalOpen(true); };
  const openEdit = (rec) => { setDraft({ ...rec }); setMode('edit'); setModalOpen(true); };

  const submit = (record) => {
    const final = { ...record, orgId: org.id, rate: Number(record.rate) || 0 };
    if (mode === 'create') addGst(final);
    else updateGst(record.id, final);
    if (showNotification) showNotification(`${mode === 'create' ? 'Created' : 'Updated'} ${final.name}`, 'success');
    setModalOpen(false);
    setVersion((v) => v + 1);
  };

  const doDelete = () => {
    if (!confirmDelete) return;
    deleteGst(confirmDelete.id);
    if (showNotification) showNotification(`Removed ${confirmDelete.name}`, 'success');
    setConfirmDelete(null);
    setVersion((v) => v + 1);
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search GST rates…"
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
            />
          </div>

        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg"
          style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}
        >
          <Plus size={14} aria-hidden="true" /> New GST rate
        </button>
      </section>

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b-2 border-slate-200">
                <th className="px-4 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">Label</th>
                <th className="px-4 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">Total Rate</th>
                <th className="px-4 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">CGST</th>
                <th className="px-4 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">SGST</th>
                <th className="px-4 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">IGST</th>
                <th className="px-4 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageList.map((g) => {
                const split = splitGst(g.rate);
                return (
                  <motion.tr
                    key={g.id}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-[var(--theme-tertiary)] last:border-0 transition-colors hover:bg-slate-50 group"
                  >
                    <td className="px-4 py-3 text-[12px] font-extrabold text-[#0d0d0d] whitespace-nowrap uppercase tracking-wider">{g.name}</td>
                    <td className="px-4 py-3 text-[12px] font-semibold text-slate-600">{g.rate}%</td>
                    <td className="px-4 py-3 text-[12px] font-medium text-slate-500">{split.cgst}%</td>
                    <td className="px-4 py-3 text-[12px] font-medium text-slate-500">{split.sgst}%</td>
                    <td className="px-4 py-3 text-[12px] font-medium text-slate-500">{g.rate}%</td>
                    <td className="px-4 py-3 text-right">
                      <RowActions>
                        <IconAction icon={Pen} label={`Edit ${g.name}`} title="Edit" onClick={() => openEdit(g)} />
                        <IconAction icon={Trash2} tone="danger" label={`Remove ${g.name}`} title="Remove" onClick={() => setConfirmDelete(g)} />
                      </RowActions>
                    </td>
                  </motion.tr>
                );
              })}
              {list.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-5 py-16 bg-white text-center">
                    <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No GST rates yet.</p>
                    <button type="button" onClick={openCreate} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>
                      <Plus size={12} /> Create the first one
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {list.length > 10 && (
          <div className="px-5 py-3 flex items-center justify-between text-[11px] font-bold text-slate-500 border-t border-[var(--theme-tertiary)] bg-slate-50 shrink-0">
            <div className="flex items-center gap-2">
              <span className="uppercase tracking-widest">Showing</span>
              <CustomSelect
                value={perPage}
                onChange={(val) => {
                  setPerPage(Number(val));
                  setPage(1);
                }}
                options={[
                  { value: 10, label: '10' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                  { value: list.length > 0 ? list.length : 200, label: 'All' }
                ]}
                className="w-16 bg-white border border-[var(--theme-tertiary)] rounded-sm px-2 py-1 text-[11px] font-bold text-slate-700 outline-none hover:border-[var(--theme-primary)] transition-colors cursor-pointer"
                menuUp={true}
              />
            </div>
            <div className="flex items-center gap-4">
              <span className="uppercase tracking-widest">
                PAGE <strong className="text-slate-800 mx-0.5">{page}</strong> OF <strong className="text-slate-800 ml-0.5">{totalPages}</strong>
              </span>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                  <ChevronLeft size={14} />
                </button>
                <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      <Modal
        open={modalOpen}
        draft={draft}
        setDraft={setDraft}
        onClose={() => setModalOpen(false)}
        onSubmit={submit}
        mode={mode}
        accent={accent}
        showNotification={showNotification}
        rates={all}
      />

      {confirmDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }}><Trash2 size={20} /></div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove {confirmDelete.name}?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">Products or services still using this rate will need a new GST selection.</p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setConfirmDelete(null)} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={doDelete} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

GstTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
